import { Modal } from 'antd';
import MarkdownWithToc from '@/components/MarkdownWithToc';

interface ApiDocModalProps {
  open: boolean;
  onClose: () => void;
}

const API_DOC_CONTENT = `
# BSP 接口使用指南

## 概述

BSP 平台提供多个对外接口，供供应商和外部系统集成。本指南详细说明各接口的用法、参数、响应格式和常见问题。

---

## 前置准备

### 1. 获取 API Key

登录 BSP 平台 → 点击右上角用户头像 → 选择"获取 API Key" → 复制并保存密钥

API Key 格式: \`bsp_ak_<64位十六进制字符>\`

⚠️ **重要**: Key 仅显示一次，请妥善保管！

### 2. 确认项目权限

确保您的账号已被授权访问目标项目。权限管理由 BSP 管理员配置。

---

# 第 1 章：对外释放文件下载接口

## 1. 概述

BSP 提供 4 个对外释放文件下载接口，支持版本查询、文件列表、批量下载和单文件下载。
所有接口都需要通过 X-API-Key Header 进行身份验证。

---

## 2. 接口参考

### 接口 1: 获取项目版本列表

**请求**
\`\`\`
GET /api/v1/external/deliverables/?project=<项目名>
Header: X-API-Key: <your-api-key>
\`\`\`

**查询参数**

| 参数 | 类型 | 必填 | 说明 |
|------|------|------|------|
| project | string | 是 | 项目代码 |

**响应成功示例**
\`\`\`json
{
  "code": 200,
  "message": "success",
  "data": {
    "project": "汉拿山-EA1C",
    "releases": [
      {
        "id": 24,
        "name": "M123",
        "vehicle_model": "EA1C",
        "description": "Latest release",
        "created_at": "2026-06-24T10:00:00Z",
        "file_count": 5
      }
    ]
  }
}
\`\`\`

---

### 接口 2: 获取版本文件列表

**请求**
\`\`\`
GET /api/v1/external/deliverables/?project=<项目名>&release_id=<版本ID>
Header: X-API-Key: <your-api-key>
\`\`\`

**查询参数**

| 参数 | 类型 | 必填 | 说明 |
|------|------|------|------|
| project | string | 是 | 项目名称 |
| release_id | int | 是 | 版本 ID（从接口 1 获取） |

**响应成功示例**
\`\`\`json
{
  "code": 200,
  "message": "success",
  "data": {
    "project": "汉拿山-EA1C",
    "release": {
      "id": 24,
      "name": "M123",
      "vehicle_model": "EA1C",
      "created_at": "2026-06-24T10:00:00Z"
    },
    "files": [
      {
        "id": 71,
        "name": "charts.rar",
        "size": 2097152,
        "file_type": "文档",
        "checksum": "4026feb6c524eb4cbfa21043af94a356",
        "download_url": "/api/v1/external/deliverables/download/71/?type=external_release"
      }
    ]
  }
}
\`\`\`

---

### 接口 3: 单文件下载（流式）

**请求**
\`\`\`
GET /api/v1/external/deliverables/download/<file_id>/?type=external_release
Header: X-API-Key: <your-api-key>
\`\`\`

**查询参数**

| 参数 | 类型 | 必填 | 说明 |
|------|------|------|------|
| file_id | int | 是 | 文件 ID（从接口 2 获取） |
| type | string | 是 | 固定为 external_release |

**响应头**
\`\`\`
Content-Type: application/octet-stream
Content-Disposition: attachment; filename="<文件名>"
Content-Length: <文件大小>
\`\`\`

**特性**
- ✅ 流式下载（不加载全文件到内存）
- ✅ 支持断点续传（HTTP Range header）
- ✅ UTF-8 文件名编码（支持中文）

---

### 接口 4: 批量下载（tar 流）

**请求**
\`\`\`
GET /api/v1/external/deliverables/batch-download/?project=<项目名>&release_id=<版本ID>
Header: X-API-Key: <your-api-key>
\`\`\`

**查询参数**

| 参数 | 类型 | 必填 | 说明 |
|------|------|------|------|
| project | string | 是 | 项目名称 |
| release_id | int | 是 | 版本 ID |

**响应头**
\`\`\`
Content-Type: application/x-tar
Content-Disposition: attachment; filename="<版本名>.tar"
\`\`\`

**用途**: 一次性下载指定版本的所有文件，打包为 tar 流

---

## 3. 错误处理

| 状态码 | 错误消息 | 原因 |
|--------|---------|------|
| 400 | 缺少 project 参数 | 未提供必填参数 |
| 401 | Unauthorized | API Key 无效或过期 |
| 403 | 无权访问此项目 | 账号无该项目权限 |
| 404 | 项目/版本/文件不存在 | 资源已删除或不存在 |
| 502 | 文件下载失败，OBS 服务异常 | 后端存储服务异常 |

---

## 4. Python 完整示例

> ⚠️ 使用前先执行: \`pip3 install requests\`

\`\`\`python
#!/usr/bin/env python3
"""
BSP 对外释放文件下载脚本 - 即插即用版本

使用步骤：
1. 修改下面的 API_KEY 和 PROJECT_NAME
2. 运行: python3 download.py
"""

import requests
import os
import sys
from datetime import datetime

# ===================== 【修改这里】 =====================
API_KEY = "bsp_ak_your_key_here"  # 替换为您的 API Key
PROJECT_NAME = "汉拿山-EA1C"       # 替换为目标项目名
# ========================================================

API_BASE_URL = "https://bsp.momenta.cn/api/v1/external"
DOWNLOAD_DIR = "./downloads"

def log(msg, level="INFO"):
    ts = datetime.now().strftime("%H:%M:%S")
    print(f"[{ts}] {level}: {msg}")

def main():
    try:
        # 验证配置
        if API_KEY == "bsp_ak_your_key_here":
            log("❌ 请修改脚本中的 API_KEY", "ERROR")
            return False

        headers = {"X-API-Key": API_KEY}

        # Step 1: 获取版本列表
        log(f"获取项目 '{PROJECT_NAME}' 的版本列表...")
        resp = requests.get(
            f"{API_BASE_URL}/deliverables/",
            headers=headers,
            params={"project": PROJECT_NAME},
            timeout=10
        )
        resp.raise_for_status()
        data = resp.json()

        if data.get("code") != 200:
            log(f"API 返回错误: {data.get('message')}", "ERROR")
            return False

        releases = data.get("data", {}).get("releases", [])
        if not releases:
            log("无可用版本", "ERROR")
            return False

        log(f"✓ 找到 {len(releases)} 个版本")

        # Step 2: 选择最新版本
        latest = releases[0]
        log(f"选择最新版本: {latest['name']} (ID: {latest['id']}, {latest['file_count']} 个文件)")

        # Step 3: 获取文件列表
        log("获取版本文件列表...")
        resp = requests.get(
            f"{API_BASE_URL}/deliverables/",
            headers=headers,
            params={"project": PROJECT_NAME, "release_id": latest["id"]},
            timeout=10
        )
        resp.raise_for_status()
        data = resp.json()

        files = data.get("data", {}).get("files", [])
        if not files:
            log("版本下无文件", "ERROR")
            return False

        log(f"✓ 找到 {len(files)} 个文件:")
        for f in files:
            size_mb = f.get("size", 0) / (1024 * 1024)
            print(f"  • {f['name']} ({size_mb:.2f} MB)")

        # Step 4: 下载所有文件
        os.makedirs(DOWNLOAD_DIR, exist_ok=True)
        success_count = 0

        for i, file_obj in enumerate(files, 1):
            file_id = file_obj["id"]
            file_name = file_obj["name"]
            file_size = file_obj.get("size", 0)

            output_path = os.path.join(DOWNLOAD_DIR, file_name)
            log(f"\\n【{i}/{len(files)}】下载: {file_name} ({file_size/1024/1024:.2f} MB)")

            try:
                resp = requests.get(
                    f"{API_BASE_URL}/deliverables/download/{file_id}/",
                    headers=headers,
                    params={"type": "external_release"},
                    stream=True,
                    timeout=600
                )
                resp.raise_for_status()

                downloaded = 0
                with open(output_path, "wb") as f:
                    for chunk in resp.iter_content(chunk_size=65536):
                        if chunk:
                            f.write(chunk)
                            downloaded += len(chunk)
                            if file_size:
                                percent = (downloaded / file_size) * 100
                                print(f"\\r  进度: {percent:.1f}%", end="", flush=True)

                print()  # 换行
                log(f"✓ 下载完成: {output_path}")
                success_count += 1

            except Exception as e:
                log(f"❌ 下载失败: {e}", "ERROR")

        # 总结
        log(f"\\n{'='*50}")
        log(f"✓ 成功下载 {success_count}/{len(files)} 个文件")
        log(f"下载目录: {os.path.abspath(DOWNLOAD_DIR)}")
        log(f"{'='*50}")

        return success_count == len(files)

    except requests.exceptions.ConnectionError:
        log("❌ 无法连接到 API 服务器，请检查网络连接", "ERROR")
        return False
    except Exception as e:
        log(f"❌ 执行出错: {e}", "ERROR")
        return False

if __name__ == "__main__":
    success = main()
    sys.exit(0 if success else 1)
\`\`\`

**快速开始**
\`\`\`bash
# 1. 保存脚本为 download.py

# 2. 安装依赖（仅需一次）
pip3 install requests

# 3. 编辑脚本，修改 API_KEY 和 PROJECT_NAME

# 4. 运行
python3 download.py
\`\`\`

**示例输出**
\`\`\`
[14:30:15] INFO: 获取项目 '汉拿山-EA1C' 的版本列表...
[14:30:15] INFO: ✓ 找到 2 个版本
[14:30:15] INFO: 选择最新版本: M123 (ID: 24, 1 个文件)
[14:30:15] INFO: 获取版本文件列表...
[14:30:15] INFO: ✓ 找到 1 个文件:
  • charts.rar (2.01 MB)

【1/1】下载: charts.rar (2.01 MB)
  进度: 100.0%
[14:30:16] INFO: ✓ 下载完成: ./downloads/charts.rar

==================================================
✓ 成功下载 1/1 个文件
下载目录: /home/user/downloads
==================================================
\`\`\`

---

## 5. Bash/curl 完整示例

\`\`\`bash
#!/bin/bash
# BSP 对外释放文件下载 - Bash 完整示例

API_BASE_URL="https://bsp.momenta.cn/api/v1/external"
API_KEY="bsp_ak_your_key_here"  # 替换为您的 API Key
PROJECT_NAME="汉拿山-EA1C"  # 替换为目标项目
DOWNLOAD_DIR="./downloads"

mkdir -p "$DOWNLOAD_DIR"

# Step 1: 获取版本列表
echo "[INFO] 获取版本列表..."
releases_json=$(curl -s -X GET \\
  "$API_BASE_URL/deliverables/?project=$PROJECT_NAME" \\
  -H "X-API-Key: $API_KEY")

# 解析 JSON（需要 jq）
latest_id=$(echo "$releases_json" | jq '.data.releases[0].id')
latest_name=$(echo "$releases_json" | jq -r '.data.releases[0].name')
echo "[INFO] 选择版本: $latest_name (ID: $latest_id)"

# Step 2: 获取文件列表
echo "[INFO] 获取文件列表..."
files_json=$(curl -s -X GET \\
  "$API_BASE_URL/deliverables/?project=$PROJECT_NAME&release_id=$latest_id" \\
  -H "X-API-Key: $API_KEY")

# 获取第一个文件信息
file_id=$(echo "$files_json" | jq '.data.files[0].id')
file_name=$(echo "$files_json" | jq -r '.data.files[0].name')
file_size=$(echo "$files_json" | jq '.data.files[0].size')

echo "[INFO] 开始下载: $file_name ($(( file_size / 1024 / 1024 )) MB)"

# Step 3: 下载文件
curl -X GET \\
  "$API_BASE_URL/deliverables/download/$file_id/?type=external_release" \\
  -H "X-API-Key: $API_KEY" \\
  -o "$DOWNLOAD_DIR/$file_name" \\
  --progress-bar

echo ""
echo "[INFO] ✓ 下载完成: $DOWNLOAD_DIR/$file_name"
\`\`\`

**运行方式**
\`\`\`bash
# 1. 修改脚本中的 API_KEY 和 PROJECT_NAME

# 2. 运行
bash download.sh

# 注意: 需要安装 jq 来解析 JSON
# Ubuntu/Debian: sudo apt-get install jq
# macOS: brew install jq
\`\`\`

---

## 6. 常见问题

**Q: API Key 过期了怎么办？**
A: API Key 永不过期。如果无法使用，请在 BSP 平台重新生成。

**Q: 支持断点续传吗？**
A: 支持。客户端可使用 HTTP Range header 请求特定字节范围。

**Q: 如何验证下载的文件完整性？**
A: 接口返回的 checksum 字段是文件的 MD5 值。下载后可用 \`md5sum\` 验证：
\`\`\`bash
md5sum charts.rar  # 对比与接口返回的 checksum 是否一致
\`\`\`

**Q: 如何获得更多项目的访问权限？**
A: 联系 BSP 管理员添加项目权限。

---

## 7. 技术支持

如有问题，请：
1. 查看错误响应中的 message 字段
2. 确认 API Key 和项目名称正确
3. 确认账号有目标项目的访问权限
4. 联系 BSP 平台管理员

---

# 第 2 章：供应商上传接口

## 1. 概述

BSP 提供供应商上传接口，支持上传产物包、Release Note、测试报告。**一个接口，双模式自适应**：网络好时直接传整个文件，网络差时自动分片续传。

\`\`\`
POST /api/v1/bcr/open/upload/
Header: X-API-Key: <your-api-key>
\`\`\`

---

## 2. 接口参考

### 模式一：直接上传（网络好，一笔完成）

**请求参数** (multipart/form-data)

| 参数 | 类型 | 必填 | 说明 |
|------|------|------|------|
| project | string | 是 | 项目名称 |
| version | string | 是 | 版本号 |
| content_type | string | 是 | artifact / release_note / test_report |
| artifact_kind | string | 推荐 | 产物子类型，根据项目支持的产物类型自行配置，如 soc_ota / mcu / switch / dbc / someip |
| file | file | 是 | 要上传的文件 |
| content | string | 文字模式 | 文字内容（仅 release_note/test_report，与 file 二选一） |

**响应示例** (HTTP 201)

\`\`\`json
{
  "project": "武岳山LK1A_26E",
  "version": "20260715",
  "content_type": "artifact",
  "artifact_kind": "mcu",
  "file_name": "mcu_v1.hex",
  "file_url": "https://obs.cn-east-3.myhuaweicloud.com/.../mcu_v1.hex",
  "file_size": 209715200,
  "status": "completed"
}
\`\`\`

---

### 模式二：分片上传（网络差，支持断点续传）

客户端将文件切成 5MB 块，逐块发送同一 URL。网络中断时只需重传失败的块。

**请求参数** (multipart/form-data)

| 参数 | 类型 | 必填 | 说明 |
|------|------|------|------|
| project | string | 是 | 项目名称 |
| version | string | 是 | 版本号 |
| content_type | string | 是 | artifact / release_note / test_report |
| chunk | int | 是 | 当前分块序号（0-based） |
| total_chunks | int | 是 | 总分块数 |
| upload_id | string | 首块❌后续✅ | 首块由服务端返回，后续必须携带 |
| file | file | 是 | 当前分块的文件数据 |

> 服务端判断：收到 chunk + total_chunks 参数 → 分片模式，否则 → 直接模式。

**首块响应** (HTTP 201)

\`\`\`json
{"upload_id": "abc123", "chunk": 0, "received_chunks": [0], "total_chunks": 3, "status": "uploading"}
\`\`\`

**中间块响应** (HTTP 200)

\`\`\`json
{"upload_id": "abc123", "chunk": 1, "received_chunks": [0, 1], "total_chunks": 3, "status": "uploading"}
\`\`\`

**最后块响应** (HTTP 201，自动组装完成)

\`\`\`json
{
  "upload_id": "abc123",
  "project": "武岳山LK1A_26E",
  "version": "20260715",
  "content_type": "artifact",
  "artifact_kind": "mcu",
  "file_name": "mcu_v1.hex",
  "file_url": "https://obs.cn-east-3.myhuaweicloud.com/.../mcu_v1.hex",
  "file_size": 209715200,
  "status": "completed"
}
\`\`\`

---

## 3. 错误处理

| 状态码 | 错误消息 | 原因 |
|--------|---------|------|
| 400 | project/version/content_type 均为必填 | 缺少必填参数 |
| 400 | content_type 无效 | 参数值不在允许范围内 |
| 400 | artifact 类型必须提供 file | artifact 类型缺少文件 |
| 401 | API Key无效 | 未提供或无效的 API Key |
| 404 | 项目不存在或已停用 | 项目名错误或项目已停用 |
| 413 | 文件大小超过限制 (500MB) | 直接模式文件过大，请用分片模式 |
| 503 | 文件存储服务暂不可用 | OBS 服务异常，稍后重试 |

---

## 4. Python 完整示例

> ⚠️ 使用前先执行：\`pip3 install requests\`

### 脚本 1：直接上传（网络好）

\`\`\`python
import os
import requests

# ★ 在这里填写你的 API Key
API_KEY = 'your-api-key-here'
BSP_URL = 'https://bsp.momenta.cn'


def upload_artifact(project, version, artifact_kind, file_path):
    """网络条件好时使用：一笔 POST 完成上传。"""
    with open(file_path, 'rb') as f:
        resp = requests.post(
            f'{BSP_URL}/api/v1/bcr/open/upload/',
            headers={'X-API-Key': API_KEY},
            data={
                'project': project,
                'version': version,
                'content_type': 'artifact',
                'artifact_kind': artifact_kind,
            },
            files={'file': (os.path.basename(file_path), f)},
            timeout=600,
        )

    if resp.status_code == 401:
        raise Exception('API Key 无效')
    resp.raise_for_status()
    result = resp.json()
    print(f"上传完成: {result['file_name']} ({result['file_size']} bytes)")
    print(f"下载地址: {result['file_url']}")
    return result


# 使用示例
result = upload_artifact(
    '武岳山LK1A_26E',
    '20260715',
    'mcu',
    './mcu_v1.2.3.hex',
)
\`\`\`

### 脚本 2：分片上传（网络差，支持断点续传）

\`\`\`python
import os
import time
import requests

# ★ 在这里填写你的 API Key
API_KEY = 'your-api-key-here'
BSP_URL = 'https://bsp.momenta.cn'


def upload_artifact_chunked(project, version, artifact_kind, file_path, chunk_size=5*1024*1024):
    """网络条件差时使用：自动分片上传，失败块自动重试。"""
    file_size = os.path.getsize(file_path)
    total_chunks = max(1, (file_size + chunk_size - 1) // chunk_size)
    upload_id = None

    print(f'文件: {os.path.basename(file_path)} ({file_size} bytes)')
    print(f'分块: {total_chunks} 块 × {chunk_size // 1024 // 1024}MB')

    with open(file_path, 'rb') as f:
        for i in range(total_chunks):
            chunk_data = f.read(chunk_size)
            while True:
                try:
                    data = {
                        'project': project,
                        'version': version,
                        'content_type': 'artifact',
                'artifact_kind': artifact_kind,
                        'chunk': str(i),
                        'total_chunks': str(total_chunks),
                    }
                    if upload_id:
                        data['upload_id'] = upload_id

                    resp = requests.post(
                        f'{BSP_URL}/api/v1/bcr/open/upload/',
                        headers={'X-API-Key': API_KEY},
                        data=data,
                        files={'file': (os.path.basename(file_path), chunk_data)},
                        timeout=300,
                    )

                    if resp.status_code == 401:
                        raise Exception('API Key 无效')

                    resp.raise_for_status()
                    result = resp.json()
                    upload_id = result['upload_id']

                    if result['status'] == 'completed':
                        print(f'上传完成!')
                        print(f'下载地址: {result["file_url"]}')
                        return result

                    print(f'  块 {i+1}/{total_chunks} OK (已收到: {result.get("received_chunks")})')
                    break

                except requests.exceptions.RequestException as e:
                    print(f'  块 {i+1}/{total_chunks} 失败 ({e})，5秒后重试...')
                    time.sleep(5)


# 使用示例
result = upload_artifact_chunked(
    '武岳山LK1A_26E',
    '20260715',
    'mcu',
    './large_mcu_firmware.hex',
)
\`\`\`

### 脚本 3：上传 Release Note / 测试报告（文字模式）

\`\`\`python
import requests

# ★ 在这里填写你的 API Key
API_KEY = 'your-api-key-here'
BSP_URL = 'https://bsp.momenta.cn'


def upload_text(project, version, content_type, text):
    """上传 Release Note 或测试报告文字内容（无需文件）。"""
    resp = requests.post(
        f'{BSP_URL}/api/v1/bcr/open/upload/',
        headers={'X-API-Key': API_KEY},
        data={
            'project': project,
            'version': version,
            'content_type': content_type,
            'content': text,
        },
        timeout=60,
    )

    if resp.status_code == 401:
        raise Exception('API Key 无效')
    resp.raise_for_status()
    result = resp.json()
    print(f"上传完成: {result['file_url']}")
    return result


# 使用示例 - Release Note
upload_text(
    '武岳山LK1A_26E',
    '20260715',
    'release_note',
    '## v1.2 Release Notes\\n\\n### 新增\\n- 功能A\\n- 功能B\\n\\n### 修复\\n- Bug #123',
)

# 使用示例 - 测试报告
upload_text(
    '武岳山LK1A_26E',
    '20260715',
    'test_report',
    '## 测试报告\\n\\n- 通过: 102/102\\n- 失败: 0\\n- 台架: RobocarL4-Dthor-003',
)
\`\`\`

---

## 5. 常见问题

**Q: 什么时候用直接上传，什么时候用分片上传？**
A: 网络稳定、文件 < 100MB 时用直接上传；网络慢或不稳定、文件较大时用分片上传。

**Q: 分片上传中网络断了怎么办？**
A: 已上传的块不受影响，只需重传失败的块。脚本已内置自动重试。

**Q: 支持上传哪些类型的文件？**
A: 支持任意文件类型。content_type 参数用于分类标记：artifact（产物包）、release_note（发布说明）、test_report（测试报告）。

**Q: API Key 从哪里获取？**
A: 登录 BSP 平台 → 点击右上角用户头像 → 选择"获取 API Key"。

**Q: 查询版本、下载产物也需要 API Key 吗？**
A: 需要。\`GET /api/v1/bcr/open/versions/\`、\`GET /api/v1/bcr/open/artifacts/\` 与上传接口一样，必须在请求头带 \`X-API-Key\`。该 Key 需属于 BSP 服务账号（如 \`rpa_service\`），普通用户 Key 返回 403，未携带返回 401。Key 获取方式同上：右上角头像 → "获取 API Key"。
`;

const ApiDocModal = ({ open, onClose }: ApiDocModalProps) => {
  return (
    <Modal
      title="BSP 接口使用指南"
      open={open}
      onCancel={onClose}
      footer={null}
      width={1100}
      style={{ top: 20 }}
      styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
    >
      <MarkdownWithToc content={API_DOC_CONTENT} />
    </Modal>
  );
};

export default ApiDocModal;
